import { customAxiosInstance } from "@/api/client";
import type {
  CanvasElement,
  PageSize,
  PrintFormatFamily,
  PrintFormatRevision,
  ReportTypeOption,
} from "./types";

const BASE_URL = "/api/v1/printing";

function normalizeArray<T>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export const printingApi = {
  listFamilies: async (): Promise<PrintFormatFamily[]> => {
    const res = await customAxiosInstance<any>({
      url: `${BASE_URL}/print-format-families/`,
      method: "GET",
    });
    return normalizeArray<PrintFormatFamily>(res);
  },

  getFamily: (id: string) =>
    customAxiosInstance<PrintFormatFamily>({
      url: `${BASE_URL}/print-format-families/${id}/`,
      method: "GET",
    }),

  createFamily: (payload: { code: string; name: string; report_type: string }) =>
    customAxiosInstance<PrintFormatFamily>({
      url: `${BASE_URL}/print-format-families/`,
      method: "POST",
      data: payload,
    }),

  listRevisions: async (familyId: string): Promise<PrintFormatRevision[]> => {
    const res = await customAxiosInstance<any>({
      url: `${BASE_URL}/print-format-revisions/`,
      method: "GET",
      params: { family: familyId },
    });
    return normalizeArray<PrintFormatRevision>(res);
  },

  getRevision: (id: string) =>
    customAxiosInstance<PrintFormatRevision>({
      url: `${BASE_URL}/print-format-revisions/${id}/`,
      method: "GET",
    }),

  createDraftRevision: (payload: {
    family: string;
    schema_json: CanvasElement[];
    page_size: PageSize;
  }) =>
    customAxiosInstance<PrintFormatRevision>({
      url: `${BASE_URL}/print-format-revisions/`,
      method: "POST",
      data: payload,
    }),

  updateDraftRevision: (
    revisionId: string,
    payload: { schema_json?: CanvasElement[]; page_size?: PageSize }
  ) =>
    customAxiosInstance<PrintFormatRevision>({
      url: `${BASE_URL}/print-format-revisions/${revisionId}/`,
      method: "PATCH",
      data: payload,
    }),

  publishRevision: (revisionId: string) =>
    customAxiosInstance<PrintFormatRevision>({
      url: `${BASE_URL}/print-format-revisions/${revisionId}/publish/`,
      method: "POST",
    }),

  setDefaultRevision: (revisionId: string) =>
    customAxiosInstance<PrintFormatRevision>({
      url: `${BASE_URL}/print-format-revisions/${revisionId}/set-default/`,
      method: "POST",
    }),

  cloneRevision: (revisionId: string) =>
    customAxiosInstance<PrintFormatRevision>({
      url: `${BASE_URL}/print-format-revisions/${revisionId}/clone/`,
      method: "POST",
    }),

  reportTypes: async (): Promise<ReportTypeOption[]> => {
    const res = await customAxiosInstance<any>({
      url: `${BASE_URL}/report-types/`,
      method: "GET",
    });
    return normalizeArray<ReportTypeOption>(res);
  },

  getRealSampleData: (reportType: string): Promise<Record<string, unknown>> =>
    customAxiosInstance<Record<string, unknown>>({
      url: `${BASE_URL}/report-types/${reportType}/sample-data/`,
      method: "GET",
    }),
};
